import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Row, Col, Badge } from "react-bootstrap";
import katex from "katex";
import "katex/dist/katex.min.css";

interface MathInputModalProps {
  show: boolean;
  onHide: () => void;
  onInsert: (latex: string, isBlock: boolean) => void;
  initialLatex?: string;
}

const EXAMPLE_FORMULAS = [
  { label: "Fraction", latex: "\\frac{a}{b}", description: "a over b" },
  { label: "Square Root", latex: "\\sqrt{x}", description: "square root" },
  { label: "Power", latex: "x^{2}", description: "x squared" },
  { label: "Subscript", latex: "x_{i}", description: "x sub i" },
  { label: "Sum", latex: "\\sum_{i=1}^{n} x_i", description: "summation" },
  { label: "Integral", latex: "\\int_{a}^{b} f(x) dx", description: "integral" },
  { label: "Limit", latex: "\\lim_{x \\to \\infty} f(x)", description: "limit" },
  { label: "Greek Letters", latex: "\\alpha, \\beta, \\gamma, \\pi", description: "Greek" },
  { label: "Einstein", latex: "E = mc^2", description: "famous equation" },
  { label: "Quadratic", latex: "x = \\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}", description: "quadratic formula" },
  { label: "Matrix", latex: "\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}", description: "2x2 matrix" },
  { label: "Derivative", latex: "\\frac{d}{dx} f(x)", description: "derivative" },
];

const SYNTAX_GUIDE = [
  { syntax: "^{...}", example: "x^{2}", description: "Superscript (power)" },
  { syntax: "_{...}", example: "x_{i}", description: "Subscript" },
  { syntax: "\\frac{a}{b}", example: "\\frac{1}{2}", description: "Fraction" },
  { syntax: "\\sqrt{...}", example: "\\sqrt{x}", description: "Square root" },
  { syntax: "\\sum", example: "\\sum_{i=1}^{n}", description: "Summation" },
  { syntax: "\\int", example: "\\int_{a}^{b}", description: "Integral" },
  { syntax: "\\alpha, \\beta...", example: "\\pi r^2", description: "Greek letters" },
  { syntax: "\\pm, \\times, \\div", example: "a \\pm b", description: "Operators" },
  { syntax: "\\leq, \\geq, \\neq", example: "x \\leq y", description: "Comparisons" },
  { syntax: "\\infty", example: "\\lim_{x \\to \\infty}", description: "Infinity" },
];

const MathInputModal: React.FC<MathInputModalProps> = ({
  show,
  onHide,
  onInsert,
  initialLatex = "",
}) => {
  const [latex, setLatex] = useState(initialLatex);
  const [isBlock, setIsBlock] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Update preview when latex changes
  useEffect(() => {
    if (!latex.trim()) {
      setPreviewHtml("");
      setError(null);
      return;
    }

    try {
      const html = katex.renderToString(latex, {
        throwOnError: true,
        displayMode: isBlock,
      });
      setPreviewHtml(html);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Invalid LaTeX");
      setPreviewHtml("");
    }
  }, [latex, isBlock]);

  // Reset when modal opens
  useEffect(() => {
    if (show) {
      setLatex(initialLatex);
      setIsBlock(false);
      setError(null);
    }
  }, [show, initialLatex]);

  const handleInsert = useCallback(() => {
    if (latex.trim() && !error) {
      onInsert(latex, isBlock);
      onHide();
    }
  }, [latex, isBlock, error, onInsert, onHide]);

  const handleExampleClick = useCallback((exampleLatex: string) => {
    setLatex(exampleLatex);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleInsert();
      }
    },
    [handleInsert]
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Insert Math Formula</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={7}>
            {/* Input Area */}
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>LaTeX Formula</strong>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={latex}
                onChange={(e) => setLatex(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter LaTeX formula, e.g., E = mc^2"
                className="font-monospace"
                autoFocus
              />
              <Form.Text className="text-muted">
                Press Enter to insert, or click the Insert button below.
              </Form.Text>
            </Form.Group>

            {/* Display Mode Toggle */}
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="block-mode-switch"
                label="Display as block (centered, larger)"
                checked={isBlock}
                onChange={(e) => setIsBlock(e.target.checked)}
              />
            </Form.Group>

            {/* Preview */}
            <div className="mb-3">
              <Form.Label>
                <strong>Preview</strong>
              </Form.Label>
              <div
                className={`math-preview-box ${error ? "error" : ""}`}
                style={{
                  minHeight: "60px",
                  padding: "16px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  border: error ? "1px solid #dc3545" : "1px solid #dee2e6",
                  textAlign: isBlock ? "center" : "left",
                }}
              >
                {error ? (
                  <span className="text-danger">{error}</span>
                ) : previewHtml ? (
                  <span dangerouslySetInnerHTML={{ __html: previewHtml }} />
                ) : (
                  <span className="text-muted">
                    Formula preview will appear here...
                  </span>
                )}
              </div>
            </div>

            {/* Quick Examples */}
            <div className="mb-3">
              <Form.Label>
                <strong>Quick Examples</strong>{" "}
                <small className="text-muted">(click to use)</small>
              </Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {EXAMPLE_FORMULAS.map((example) => (
                  <Badge
                    key={example.latex}
                    bg="light"
                    text="dark"
                    className="example-badge"
                    style={{
                      cursor: "pointer",
                      padding: "8px 12px",
                      fontSize: "0.85rem",
                      border: "1px solid #dee2e6",
                    }}
                    onClick={() => handleExampleClick(example.latex)}
                    title={example.description}
                  >
                    {example.label}
                  </Badge>
                ))}
              </div>
            </div>
          </Col>

          <Col md={5}>
            {/* Syntax Guide */}
            <div className="syntax-guide">
              <Form.Label>
                <strong>LaTeX Syntax Guide</strong>
              </Form.Label>
              <div
                style={{
                  maxHeight: "350px",
                  overflowY: "auto",
                  fontSize: "0.85rem",
                }}
              >
                <table className="table table-sm table-striped">
                  <thead>
                    <tr>
                      <th>Syntax</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SYNTAX_GUIDE.map((item) => (
                      <tr
                        key={item.syntax}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleExampleClick(item.example)}
                        title={`Click to use: ${item.description}`}
                      >
                        <td>
                          <code>{item.syntax}</code>
                        </td>
                        <td>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: katex.renderToString(item.example, {
                                throwOnError: false,
                              }),
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2">
                <small className="text-muted">
                  <strong>Tips:</strong>
                  <ul className="mb-0 ps-3">
                    <li>Use curly braces {"{ }"} to group expressions</li>
                    <li>Backslash \ starts special commands</li>
                    <li>Greek: \alpha, \beta, \gamma, \pi, etc.</li>
                    <li>
                      <a
                        href="https://katex.org/docs/supported.html"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Full KaTeX reference
                      </a>
                    </li>
                  </ul>
                </small>
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleInsert}
          disabled={!latex.trim() || !!error}
        >
          Insert Formula
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MathInputModal;
